import React, { useEffect, useState, useRef } from 'react';
import { Divider, Stack, Box, Typography as Muitypo, Card, Grid, Skeleton, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
import { PATHS, GLOBAL_USER } from '../../utils/constants';
import { Button, Label, Link, Tabs, TextField, Typography, Chip } from '../../components';

import VehicleCompare from '../VehicleCompare';
import { GetAssignmentRes } from '../../types/assignments';
import { approveAssignment, getAssignments, rejectAssignment, retryOnHoldAssignment } from '../../services/assignment';
import useConfirm from '../../hooks/useConfirm';
import useSnackbarNotifications from '../../hooks/useSnackbarNotifications';
import { Center } from '../../components/Containers';
import { LoadingDialogue } from '../../components/Spinners';
import { usePermissions } from '../../hooks/usePermissions';
import useHeight from '../../hooks/useHeight';

const Loader = ({ qty = 7 }) => {
  return (
    <Grid container p={1}>
      <Grid xs={5}>
        {Array.from({ length: qty }).map(() => (
          <Skeleton variant="rectangular" width="100%" height={60} sx={{ my: 1, borderRadius: 2 }} />
        ))}
      </Grid>
      <Grid xs={7} pl={2}>
        <Skeleton variant="rectangular" width="100%" height={500} sx={{ my: 1, borderRadius: 2 }} />
      </Grid>
    </Grid>
  );
};

interface TPendingAssignment extends GetAssignmentRes {
  decision?: 'Rejected' | 'Approved';
}

const PendingAssignments = () => {
  const { confirm } = useConfirm();
  const { hasPermissions } = usePermissions();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarNotifications();
  const [assignments, setAssignments] = useState<TPendingAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAssignmentIdx, setSelectedAssignmentIdx] = useState(0);
  const [decisionPending, setDecisionPending] = useState(false);
  const [transactionErrors, setTransactionErrors] = useState<any>();
  const selectedAssignment = assignments[selectedAssignmentIdx];
  const elementDOM = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const { height } = useHeight(elementDOM, loaded);
  let reason = '';
  useEffect(() => {
    setLoading(true);
    getAssignments({ status: 'Pending' })
      .then((res) => {
        setAssignments(res);
      })
      .catch()
      .finally(() => setLoading(false));
  }, []);

  const handleLoad = () => {
    setLoaded(true);
  };

  const handleChangeReason = (nr: string) => {
    reason = nr;
  };

  const ReasonInput = (
    <Box my={1}>
      <Muitypo variant="caption">Add Reason</Muitypo>
      <TextField
        sx={{ bg: (theme) => theme.palette.common.white }}
        placeholder="Reason..."
        onChange={(e) => handleChangeReason(e.target.value)}
      />
    </Box>
  );

  const ApproveAssignmentBody = (
    <Stack py={1} spacing={2}>
      {(selectedAssignment?.customerVehicle?.vin || selectedAssignment?.customerVehicle?.batchNumber) && (
        <Box
          p={1}
          mx={1}
          sx={{
            color: '#c3493b',
            display: 'flex',
            border: '1px solid #c3493b',
            borderRadius: 2,
            alignItems: 'center',
          }}
        >
          <Icon icon="charm:circle-warning" color="#c3493b" />
          <Muitypo variant="caption" ml={1}>
            Customer Vehicle will be added to inventory when approved
          </Muitypo>
        </Box>
      )}
      {selectedAssignment?.customerVehicle?.vin && (
        <Stack>
          <Muitypo variant="body1">VIN</Muitypo>
          <Muitypo variant="caption">{selectedAssignment?.customerVehicle.vin}</Muitypo>
        </Stack>
      )}
      {selectedAssignment?.customerVehicle?.batchNumber && (
        <Stack>
          <Muitypo variant="body1">Batch Number</Muitypo>
          <Muitypo variant="caption">{selectedAssignment?.customerVehicle.batchNumber}</Muitypo>
        </Stack>
      )}
    </Stack>
  );

  const onReject = async () => {
    const isConfirmed = await confirm({
      title: 'Reject Assignment',
      description: 'Are you sure you want to reject this request? You can view this request in your reject list.',
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, Reject',
      confirmBody: ReasonInput,
    });
    if (isConfirmed) {
      setDecisionPending(true);
      try {
        if (!reason) {
          showErrorSnackbar({ message: 'Please provide reason for rejection.' });
          return;
        }
        const data = {
          requestId: selectedAssignment?.requestNumber,
          reason,
          requestor: GLOBAL_USER,
        };
        const rejectAssignmentResp = await rejectAssignment(data);

        setAssignments((prev) => {
          const rmd = prev.filter((a) => a?.requestNumber !== data.requestId);
          return rmd;
        });
        if (rejectAssignmentResp.message !== 'Success') {
          showErrorSnackbar({ message: rejectAssignmentResp.message });
        } else {
          showSuccessSnackbar({
            message: `The request ${selectedAssignment.requestNumber} has been rejected successfully.`,
          });
        }
      } catch (error) {
        showErrorSnackbar({ message: 'Something went wrong. Try again.' });
      } finally {
        setDecisionPending(false);
        reason = '';
      }
    } else {
      reason = '';
    }
  };

  const onApprove = async () => {
    const isConfirmed = await confirm({
      title: 'Approve Assignment',
      description: `"${selectedAssignment?.requestNumber}" is pending request. Are you sure you want to approve it?`,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, Approve',
      confirmBody: ApproveAssignmentBody,
    });
    if (isConfirmed) {
      setDecisionPending(true);
      try {
        const requestId = selectedAssignment?.requestNumber || '';
        const res = await approveAssignment(requestId, GLOBAL_USER);

        if (res.message === 'Success') {
          setDecisionPending(false);
          setAssignments((prev) => {
            const rmd = prev.filter((a) => a?.requestNumber !== requestId);
            return rmd;
          });
          showSuccessSnackbar({
            message: `The request ${selectedAssignment?.requestNumber} has been approved successfully.`,
          });
        } else if (res?.sdfcResponse?.data || res?.vmsResponse?.data) {
          setDecisionPending(false);
          setTransactionErrors(res);
          // showErrorSnackbar({ message: res.message });
        } else {
          setDecisionPending(false);
          showErrorSnackbar({ message: res.message });
        }
      } catch (error) {
        showErrorSnackbar({ message: 'Something went wrong. Try again.' });
      }
      // finally {
      //   setDecisionPending(false);
      // }
    }
  };

  const retryAssignment = async () => {
    setDecisionPending(true);

    try {
      const requestId = selectedAssignment?.requestNumber || '';
      const res = await approveAssignment(requestId, GLOBAL_USER, true);

      if (res.message === 'Success') {
        setDecisionPending(false);
        setAssignments((prev) => {
          const rmd = prev.filter((a) => a?.requestNumber !== requestId);
          return rmd;
        });
        showSuccessSnackbar({
          message: `The request ${selectedAssignment?.requestNumber} has been approved successfully.`,
        });
      } else if (res?.sdfcResponse?.data || res?.vmsResponse?.data) {
        setDecisionPending(false);
        setTransactionErrors(res);
        // showErrorSnackbar({ message: res.message });
      } else {
        setDecisionPending(false);
        showErrorSnackbar({ message: res.message });
      }
    } catch (error) {
      showErrorSnackbar({ message: 'Something went wrong. Try again.' });
    }
  };

  if (loading) {
    return <Loader />;
  }

  console.log(transactionErrors);

  return (
    <>
      <LoadingDialogue open={decisionPending} />
      <Grid container spacing={2}>
        <Grid item xs={5}>
          <Box pl={0} mt={1} pb={3} ml={1} sx={{ overflowY: 'auto', height: `${height}px` }}>
            {assignments?.length ? (
              <>
                {assignments.map((assignment, idx) => (
                  <Card
                    key={assignment?.requestNumber}
                    elevation={2}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: () =>
                        assignment?.requestNumber === selectedAssignment?.requestNumber ? '#eee' : '#fff',
                      my: 1,
                      padding: '15px 15px 15px 15px',
                      borderColor: (theme) =>
                        assignment.requestNumber === selectedAssignment?.requestNumber
                          ? theme.palette.primary.main
                          : null,
                    }}
                    onClick={() => setSelectedAssignmentIdx(idx)}
                  >
                    <Grid container>
                      <Grid item xs={12} md={6} lg={2}>
                        <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                          <Typography weight={700} size={10} color="#3e3e3e">
                            REQUEST NUMBER
                          </Typography>
                          <Typography weight={400} size={14}>
                            {assignment?.requestNumber}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={2} lg={2.5}>
                        <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                          <Typography weight={700} size={10} color="#3e3e3e">
                            {assignment.inventoryVehicle.vin ? 'VIN' : 'BATCH NUMBER'}
                          </Typography>
                          <Typography weight={400} size={14} sx={{ width: '100%', wordWrap: 'break-word' }}>
                            {assignment.inventoryVehicle.vin
                              ? assignment.inventoryVehicle.vin
                              : assignment.inventoryVehicle.batchNumber}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={6} lg={2}>
                        <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                          <Typography weight={700} size={10} color="#3e3e3e">
                            CUSTOMER NAME
                          </Typography>
                          <Typography weight={400} size={14} color="#3E3E3E">
                            {assignment.customerName}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={6} lg={2}>
                        <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                          <Typography weight={700} size={10} color="#3e3e3e">
                            REQUESTED DATE
                          </Typography>
                          <Typography weight={400} size={14} color="#3E3E3E">
                            {assignment.requestDate}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={6} lg={2}>
                        <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                          <Typography weight={700} size={10} color="#3e3e3e">
                            REQUESTED BY
                          </Typography>
                          <Typography weight={400} size={14} color="#3E3E3E">
                            {assignment.requestedBy}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid lg={1}>
                        <Stack direction="row" alignItems="center" justifyContent="end" sx={{ height: '100%' }}>
                          {(assignment?.transactionErrors?.requestId || transactionErrors?.requestId) && (
                            <Icon icon="mdi:flag" width="20px" height="20px" color="#E04732" />
                          )}
                          <IconButton onClick={() => setSelectedAssignmentIdx(idx)}>
                            <Icon icon="akar-icons:chevron-right" fontSize={20} color="#456BD9" />
                          </IconButton>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Card>
                ))}
              </>
            ) : (
              <Box p={0.5} mt={1}>
                <Center>
                  <Muitypo variant="body1">No requests found.</Muitypo>
                </Center>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid xs={7}>
          {selectedAssignment && (
            <Box
              p={(theme) => theme.spacing(2.5, 4.6, 3, 4.6)}
              m={(theme) => theme.spacing(4, 1, 0, 2.6)}
              borderRadius={1.5}
              bgcolor={(theme) => theme.palette.grey[50]}
              border={(theme) => `1px solid #E3E3E3`}
              ref={elementDOM}
              className="box"
              onLoad={handleLoad}
            >
              <Stack direction="row" alignItems="flex-end">
                <Stack direction="row" justifyContent="space-between" width="100%">
                  <Muitypo variant="h4" fontWeight={500} fontSize={32} color="#1F1F1F">
                    {selectedAssignment?.requestNumber}
                  </Muitypo>
                  {hasPermissions(['PERFORM_APPROVE_REJECT_VEHICLE_ASSIGNMENT_REQUEST']) && (
                    <Box alignItems="flex-end">
                      {selectedAssignment.decision ? (
                        <Button disabled>{selectedAssignment.decision}</Button>
                      ) : (
                        <>
                          <Button
                            disabled={
                              decisionPending ||
                              !(
                                selectedAssignment?.transactionErrors?.sfdcResponse?.statusCode !== 200 &&
                                selectedAssignment?.transactionErrors?.vmsResponse?.statusCode !== 200
                              )
                            }
                            onClick={onReject}
                            variant="outlined"
                            sx={{
                              fontSize: 14,
                              fontWeight: 400,
                              color: '#456BD9',
                              textTransform: 'none',
                              padding: '6px 20px',
                            }}
                          >
                            Reject
                          </Button>
                          {hasPermissions(['PERFORM_VEHICLE_ASSIGNMENT_REQUEST_APPROVAL']) && (
                            <Button
                              disabled={
                                decisionPending ||
                                selectedAssignment?.transactionErrors?.requestId ||
                                transactionErrors?.requestId
                              }
                              variant="contained"
                              onClick={onApprove}
                              sx={{
                                fontSize: 14,
                                fontWeight: 400,
                                color: '#fff',
                                textTransform: 'none',
                                padding: '7px 20px',
                                boxShadow: 'none',
                                ml: 1.2,
                              }}
                            >
                              Approve
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Stack>
                <Link
                  to={PATHS.vehicles}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}
                >
                  {/* {summeryMetaData?.reassignLabel} <Icon icon="akar-icons:chevron-down" /> */}
                </Link>
                <Box flexGrow={1} />
                <Link to={PATHS.vahicle('123')} sx={{ mb: 0.5 }}>
                  {/* {summeryMetaData?.linkLabel} */}
                </Link>
              </Stack>
              <Box my={1}>
                {selectedAssignment.comments && (
                  <Stack sx={{ bgcolor: '#f2f2f2', p: 1, borderRadius: 1 }}>
                    <Muitypo variant="caption" sx={{ fontSize: 10, color: (theme) => theme.palette.grey[600] }}>
                      Reason for request
                    </Muitypo>
                    <Muitypo variant="caption">{selectedAssignment?.comments}</Muitypo>
                  </Stack>
                )}
              </Box>
              <VehicleCompare
                customerVehicle={selectedAssignment?.customerVehicle}
                inventoryVehicle={selectedAssignment?.inventoryVehicle}
                transactionErrors={transactionErrors || selectedAssignment?.transactionErrors}
                retryCall={retryAssignment}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default PendingAssignments;
